'use server';

import { cookies } from 'next/headers';
import { redirect } from 'next/navigation';
import createClient from "openapi-fetch";
import type { paths } from "@/app/api/schema";
import { decodeSessionUserToken } from "@/lib/session-token";
import { getAuthToken } from "@/lib/auth";
import type { AuthContext, LoginState } from "@/types/auth";

const SERVICE_URL = process.env.AUTOMATARI_SERVICE_URL;
const AUTH_CONTEXTS_TIMEOUT_MS = 5_000;

export async function loginAction(_: LoginState, formData: FormData): Promise<LoginState> {
  const email = String(formData.get('email') ?? '').trim();
  const password = String(formData.get('password') ?? '');

  if (!email || !password) {
    return { ok: false, message: 'Completa correo y contraseña.' };
  }

  if (!SERVICE_URL) {
    console.error("loginAction: AUTOMATARI_SERVICE_URL is not configured");
    return {
      ok: false,
      message: "La autenticación no está disponible en este momento.",
    };
  }

  let data: paths["/api/auth/login"]["post"]["responses"][200]["content"]["application/json"] | undefined;
  let error: unknown;

  try {
    const client = createClient<paths>({
      baseUrl: SERVICE_URL,
    });

    ({ data, error } = await client.POST("/api/auth/login", {
      body: {
        email,
        password,
      },
    }));
  } catch (error) {
    console.error("loginAction failed", error);
    return {
      ok: false,
      message: "Error de conexión con el servidor.",
    };
  }

  if (error || !data) {
    return {
      ok: false,
      message: 'No se pudo iniciar sesión. Verifica tus credenciales.',
    };
  }

  const token = data.access_token;
  const payload = decodeSessionUserToken(token);
  if (!payload) {
    return { ok: false, message: "No se pudo iniciar sesión. Intenta de nuevo." };
  }

  const secure = process.env.NODE_ENV === 'production';
  (await cookies()).set('access_token', token, {
    httpOnly: true,
    sameSite: 'lax',
    secure,
    path: '/',
    maxAge: 24 * 60 * 60,
  });

  if (payload.is_platform_admin) {
    redirect("/platform/dashboard");
  }

  if (payload.role_name === 'admin') {
    redirect("/console/dashboard");
  }

  redirect("/portal/dashboard");

  return { ok: false, message: 'Error desconocido al iniciar sesión.' };
}

export async function logoutAction() {
  const secure = process.env.NODE_ENV === 'production';

  // Clear the JWT token cookie
  (await cookies()).set('access_token', '', {
    httpOnly: true,
    sameSite: 'lax',
    secure,
    path: '/',
    maxAge: 0,
  });

  // Redirect to login page
  redirect('/login');
}

async function setAccessTokenCookie(token: string) {
  const secure = process.env.NODE_ENV === 'production';
  (await cookies()).set('access_token', token, {
    httpOnly: true,
    sameSite: 'lax',
    secure,
    path: '/',
    maxAge: 24 * 60 * 60,
  });
}

export async function getAuthContexts(): Promise<AuthContext[]> {
  const token = await getAuthToken();
  if (!token || !SERVICE_URL) return [];

  let res: Response;
  try {
    res = await fetch(`${SERVICE_URL}/api/auth/contexts`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: "no-store",
      signal: AbortSignal.timeout(AUTH_CONTEXTS_TIMEOUT_MS),
    });
  } catch (error) {
    // Los contextos solo alimentan el selector de empresa; la consola puede
    // mostrarse sin ellos si el servicio tarda en responder.
    if (!(error instanceof Error && error.name === "TimeoutError")) {
      console.warn("getAuthContexts unavailable");
    }
    return [];
  }

  if (!res.ok) return [];
  return res.json();
}

export async function switchCompanyAction(formData: FormData) {
  const companyId = String(formData.get("company_id") ?? "");
  const token = await getAuthToken();
  if (!companyId || !token || !SERVICE_URL) {
    redirect("/login");
  }

  const res = await fetch(`${SERVICE_URL}/api/auth/switch-company`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ company_id: companyId }),
  });

  if (!res.ok) {
    redirect("/403");
  }

  const data: { access_token?: string } = await res.json();
  if (!data.access_token) {
    redirect("/login");
  }

  await setAccessTokenCookie(data.access_token);
  const payload = decodeSessionUserToken(data.access_token);
  if (payload?.role_name === "admin") {
    redirect("/console/dashboard");
  }
  redirect("/portal/dashboard");
}

export async function stopImpersonationAction() {
  const token = await getAuthToken();
  if (!token || !SERVICE_URL) {
    redirect("/login");
  }

  const res = await fetch(`${SERVICE_URL}/api/auth/impersonations/stop`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    redirect("/login");
  }

  const data: { access_token?: string } = await res.json();
  if (!data.access_token) {
    redirect("/login");
  }

  await setAccessTokenCookie(data.access_token);
  redirect("/platform/dashboard");
}

export async function startImpersonationAction(formData: FormData) {
  const companyId = String(formData.get("company_id") ?? "");
  const token = await getAuthToken();
  if (!companyId || !token || !SERVICE_URL) {
    redirect("/login");
  }

  const res = await fetch(`${SERVICE_URL}/api/auth/impersonations`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      company_id: companyId,
      reason: "Soporte desde plataforma",
    }),
  });

  if (!res.ok) {
    redirect("/403");
  }

  const data: { access_token?: string } = await res.json();
  if (!data.access_token) {
    redirect("/login");
  }

  await setAccessTokenCookie(data.access_token);
  redirect("/console/dashboard");
}
